"use client";

import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { useTranslation } from "react-i18next";
import { ApiError, api, loadTokens } from "@/lib/api";

/**
 * EPIC-045 part 4b — "Submit article" button + inline composer for the
 * News section on /topic/{slug}.
 *
 * Anonymous viewers see the button but click → /login?next=… so they
 * don't waste a click filling the form. Signed-in users get the
 * inline composer below the button: URL + title + optional source.
 *
 * On submit:
 *   - 200 + article → "Submitted for review" toast
 *   - 200 + article=null → "Already submitted" toast (re-submit conflict)
 *   - 400/404 → inline error
 *
 * Successful submissions land in the admin inbox under the
 * `news_submission` source (part 4) — admin then approves via
 * PATCH /v1/admin/news-articles/:id.
 */
export function SubmitArticleButton({ slug }: { slug: string }) {
  const { t } = useTranslation();
  const router = useRouter();
  const [signedIn, setSignedIn] = useState<boolean | null>(null);
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [title, setTitle] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{
    kind: "ok" | "err";
    msg: string;
  } | null>(null);

  useEffect(() => {
    setSignedIn(loadTokens() !== null);
  }, []);

  if (signedIn === null) return null;

  function handleClick() {
    if (!signedIn) {
      router.push(`/login?next=/topic/${slug}`);
      return;
    }
    setOpen(true);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (url.trim().length === 0 || title.trim().length === 0) {
      setStatus({ kind: "err", msg: t("submitArticle.errRequired") });
      return;
    }
    setSubmitting(true);
    setStatus(null);
    try {
      const res = await api.submitTopicNewsArticle(slug, {
        url: url.trim(),
        title: title.trim(),
        sourceName: sourceName.trim() || undefined,
      });
      if (res.article === null) {
        setStatus({
          kind: "ok",
          msg: t("submitArticle.msgAlreadySubmitted"),
        });
      } else {
        setStatus({ kind: "ok", msg: t("submitArticle.msgSubmitted") });
        setUrl("");
        setTitle("");
        setSourceName("");
      }
    } catch (err) {
      setStatus({
        kind: "err",
        msg:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : t("submitArticle.errFailed"),
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-2">
      <button
        type="button"
        onClick={handleClick}
        className="rounded-md border border-[var(--color-border)] px-3 py-1 text-[11px] font-bold text-[var(--color-text2)] transition-colors hover:border-[var(--color-brand)]"
      >
        {t("submitArticle.cta")}
      </button>
      {open && (
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-md rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3"
        >
          <div className="space-y-2">
            <input
              type="url"
              required
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t("submitArticle.urlPlaceholder")}
              className="w-full rounded border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-[12px] text-[var(--color-text)] focus:border-[var(--color-brand)] focus:outline-none"
            />
            <input
              type="text"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value.slice(0, 500))}
              maxLength={500}
              placeholder={t("submitArticle.titlePlaceholder")}
              className="w-full rounded border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-[12px] text-[var(--color-text)] focus:border-[var(--color-brand)] focus:outline-none"
            />
            <input
              type="text"
              value={sourceName}
              onChange={(e) => setSourceName(e.target.value.slice(0, 100))}
              maxLength={100}
              placeholder={t("submitArticle.sourcePlaceholder")}
              className="w-full rounded border border-[var(--color-border)] bg-transparent px-2 py-1.5 text-[12px] text-[var(--color-text)] focus:border-[var(--color-brand)] focus:outline-none"
            />
          </div>
          {status && (
            <p
              className={`mt-2 text-[10px] ${status.kind === "ok" ? "text-emerald-400" : "text-red-400"}`}
            >
              {status.msg}
            </p>
          )}
          <div className="mt-2 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setStatus(null);
              }}
              className="text-[10px] text-[var(--color-text3)] hover:text-[var(--color-text2)]"
            >
              {t("submitArticle.close")}
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-md px-3 py-1 text-[11px] font-bold text-white disabled:opacity-50"
              style={{ background: "var(--color-brand)" }}
            >
              {submitting
                ? t("submitArticle.submitting")
                : t("submitArticle.submit")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
